import { APP_BASE_HREF } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import { LOCALE_ID, NgModule, Injector } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from "ngx-translate-multi-http-loader";
import { ServicesModule, AUDITLIB_CONFIG, Amap3Module, GeneralFilterPipe, TableFilterPipe, DatePipe, DeepObjectFilterPipe, SortPipe } from '@mapfre-tech/amap3';
import { ToastModule } from 'primeng/toast';
import { environment } from '../environments/environment';
import { CoreModule } from './@core/core.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MsalModule, MsalInterceptor, MsalInterceptorConfiguration, MsalGuardConfiguration, MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalService, MsalGuard, MsalBroadcastService, MsalRedirectComponent } from '@azure/msal-angular';
import localeEs from '@angular/common/locales/es';
import localeEn from '@angular/common/locales/en';
import { InjectorService } from './@core/data/injector/injector.service';
import { LogLevel, IPublicClientApplication, PublicClientApplication, BrowserCacheLocation, InteractionType } from '@azure/msal-browser';

import { GuardsModule } from './@core/guards/guards.module';
import { AuthInterceptor } from './@core/interceptors/auth.interceptor';
import { InterceptorsModule } from './@core/interceptors/interceptors.module';
import { LoadingInterceptor } from './@core/interceptors/loading.interceptor';

registerLocaleData(localeEs, 'es')
registerLocaleData(localeEn, 'en')

// El select de @mapfre-tech/amap3 invoca translate.instant() con el valor del campo de cada opcion;
// si alguna opcion trae el campo vacio ("") ngx-translate lanza 'Parameter "key" required'.
const originalInstant = TranslateService.prototype.instant;
TranslateService.prototype.instant = function (this: TranslateService, key: string | string[], interpolateParams?: object) {
  if (key == null || key === '' || (Array.isArray(key) && key.length === 0)) {
    return key ?? '';
  }
  return originalInstant.call(this, key, interpolateParams);
};

const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
  // acquireTokenSilent rejection on startup is expected behaviour (MSAL triggers interactive flow to resolve)
  if (message.includes('acquireTokenSilent rejected')) return;
  switch (logLevel) {
    case LogLevel.Error:
      console.error(message);
      break;
    case LogLevel.Warning:
      console.warn(message);
      break;
    default:
      console.log(message);
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId, // application ID
      authority: environment.authority, // tenant ID
      // validateAuthority: true,
      redirectUri: environment.redirectUri,
      postLogoutRedirectUri: environment.redirectUri,
      //navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false
      }
    }
  });
}

// MsalInterceptor will obtain tokens and add them to all your Http requests in API calls based on the protectedResourceMap
//lista de direcciones protegidas (con token) de mi API si uso interceptors
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string> | null>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set('./assets/i18n/*.json', null);
  protectedResourceMap.set(environment.base_URL, [environment.scope]);
  protectedResourceMap.set(environment.base_URL_GAIA, [environment.scope]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    authRequest: {
      extraScopesToConsent: [
            'user.read',
            'openid',
            'profile',
            environment.scope
          ]
    }

    
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { interactionType: InteractionType.Redirect };
}


// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: `${environment.base_URL}/translations/`, suffix: "" },
    // version as query param so browsers don't reuse cached translations from a previous release
    // (CloudFront ignores query strings in its cache key, but each deploy invalidates /*)
    { prefix: "./assets/i18n/", suffix: `.json?v=${environment.version}` },
  ]);
}
@NgModule({ declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent, MsalRedirectComponent], imports: [BrowserModule,
        AppRoutingModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            },
            useDefaultLang: false
        }),
        GuardsModule.forRoot(),
        ServicesModule.forRoot(environment),
        ToastModule,
        Amap3Module,
        InterceptorsModule,
        CoreModule,
        MsalModule], providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        GeneralFilterPipe,
        TableFilterPipe,
        DatePipe,
        DeepObjectFilterPipe,
        SortPipe,
      {
        provide: AUDITLIB_CONFIG,
        useValue: {
          apiBaseUrl: environment.base_URL,
          gaiaBaseUrl: environment.base_URL_GAIA,
          translationsBaseUrl: environment.base_URL + '/translations'
        }
      },
        { provide: LOCALE_ID, useValue: 'es' },
        { provide: APP_BASE_HREF, useValue: '/' },
        provideAnimations(),
        provideHttpClient(withInterceptors([AuthInterceptor, LoadingInterceptor]), withInterceptorsFromDi()),
    ] })
export class AppModule {
  constructor(private injector: Injector) {
    // Create global Service Injector.
    InjectorService.injector = this.injector;
  }
}


